<template>
  <div class="fullscreen-page">
    <HeaderMenu title="User Management" />
    <main class="page-content">
      <!-- Create User Button -->
      <div class="action-bar">
        <button @click="showCreateUserDialog = true" class="primary btn">
          ＋ Create User
        </button>
      </div>

      <!-- Users Table -->
      <div v-if="users.length > 0" class="users-table">
        <div class="table-header">
          <div class="col-username">Username</div>
          <div class="col-groups">Groups</div>
          <div class="col-role">Role</div>
          <div class="col-actions">Actions</div>
        </div>
        <div v-for="u in users" :key="u.id" class="table-row">
          <div class="col-username">{{ u.username }}</div>
          <div class="col-groups">{{ u.group_count }}</div>
          <div class="col-role">
            <span class="role-badge" :class="{ admin: u.is_admin }">
              {{ u.is_admin ? 'Admin' : 'User' }}
            </span>
          </div>
          <div class="col-actions">
            <button
              @click="toggleRoleDialog(u)"
              :disabled="u.id === currentUserId"
              class="small btn"
              :class="{ 'primary btn': !u.is_admin, 'secondary btn': u.is_admin }"
            >
              {{ u.is_admin ? 'Make User' : 'Make Admin' }}
            </button>
            <button
              @click="resetPasswordDialog(u)"
              :disabled="u.id === currentUserId"
              class="small secondary btn"
            >
              Reset Password
            </button>
            <button
              @click="deleteUserDialog(u)"
              :disabled="u.id === currentUserId"
              class="small danger btn"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
      <div v-else class="empty-state">
        <p>No users found</p>
      </div>
    </main>

    <!-- Create User Modal -->
    <div v-if="showCreateUserDialog" class="modal-overlay" @click="showCreateUserDialog = false">
      <div class="modal" @click.stop>
        <h2>Create New User</h2>
        <div class="modal-form">
          <div class="form-group">
            <label>Username</label>
            <input v-model="newUserForm.username" type="text" placeholder="Enter username" />
            <span v-if="newUserForm.username && newUserForm.username.length < 3" class="error">
              Username must be at least 3 characters
            </span>
          </div>
          <div class="form-group">
            <label>Password</label>
            <input v-model="newUserForm.password" type="password" placeholder="Enter password" />
            <span v-if="newUserForm.password && newUserForm.password.length < 6" class="error">
              Password must be at least 6 characters
            </span>
          </div>
          <div v-if="newUserError" class="error">{{ newUserError }}</div>
          <div class="modal-buttons">
            <button @click="createUser" class="primary btn" :disabled="isCreatingUser">
              {{ isCreatingUser ? 'Creating...' : 'Create User' }}
            </button>
            <button @click="showCreateUserDialog = false" class="secondary btn">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Reset Password Modal -->
    <div v-if="showResetPasswordDialog" class="modal-overlay" @click="showResetPasswordDialog = false">
      <div class="modal" @click.stop>
        <h2>Reset Password for {{ selectedUser?.username }}</h2>
        <div class="modal-form">
          <div class="form-group">
            <label>New Password</label>
            <input v-model="newPassword" type="password" placeholder="Enter new password" />
            <span v-if="newPassword && newPassword.length < 6" class="error">
              Password must be at least 6 characters
            </span>
          </div>
          <div v-if="resetPasswordError" class="error">{{ resetPasswordError }}</div>
          <div class="modal-buttons">
            <button @click="confirmResetPassword" class="primary btn" :disabled="isResettingPassword">
              {{ isResettingPassword ? 'Resetting...' : 'Reset Password' }}
            </button>
            <button @click="showResetPasswordDialog = false" class="secondary btn">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Dialogs -->
    <div v-if="showConfirmDialog" class="modal-overlay" @click="showConfirmDialog = false">
      <div class="modal" @click.stop>
        <h2>{{ confirmDialogTitle }}</h2>
        <p>{{ confirmDialogMessage }}</p>
        <div class="modal-buttons">
          <button @click="confirmAction" class="danger btn" :disabled="isProcessing">
            {{ isProcessing ? 'Processing...' : 'Confirm' }}
          </button>
          <button @click="showConfirmDialog = false" class="secondary btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '../composables/useAuth'
import { API } from '../api/client'
import HeaderMenu from '../components/HeaderMenu.vue'

interface UserInfo {
  id: string
  username: string
  is_admin: boolean
  group_count: number
  created_at: string
}

const router = useRouter()
const { user: currentUser } = useAuth()
const users = ref<UserInfo[]>([])
const currentUserId = computed(() => currentUser.value?.id ?? '')
const isAdmin = computed(() => currentUser.value?.is_admin ?? false)

// Create User
const showCreateUserDialog = ref(false)
const newUserForm = ref({ username: '', password: '' })
const newUserError = ref('')
const isCreatingUser = ref(false)

// Reset Password
const showResetPasswordDialog = ref(false)
const selectedUser = ref<UserInfo | null>(null)
const newPassword = ref('')
const resetPasswordError = ref('')
const isResettingPassword = ref(false)

// Confirm Dialog
const showConfirmDialog = ref(false)
const confirmDialogTitle = ref('')
const confirmDialogMessage = ref('')
const isProcessing = ref(false)
let pendingAction: (() => Promise<void>) | null = null

onMounted(async () => {
  // Check if user is admin
  if (!isAdmin.value) {
    router.push('/')
    return
  }
  
  await loadUsers()
})

async function loadUsers() {
  try {
    const data = await API.getUsers()
    users.value = data
  } catch (error) {
    console.error('Failed to load users:', error)
  }
}

async function createUser() {
  newUserError.value = ''
  
  if (!newUserForm.value.username || newUserForm.value.username.length < 3) {
    newUserError.value = 'Username must be at least 3 characters'
    return
  }
  
  if (!newUserForm.value.password || newUserForm.value.password.length < 6) {
    newUserError.value = 'Password must be at least 6 characters'
    return
  }

  isCreatingUser.value = true
  try {
    await API.createUser(newUserForm.value.username, newUserForm.value.password)
    showCreateUserDialog.value = false
    newUserForm.value = { username: '', password: '' }
    await loadUsers()
  } catch (error: any) {
    newUserError.value = error.response?.data?.error || 'Failed to create user'
  } finally {
    isCreatingUser.value = false
  }
}

function toggleRoleDialog(u: UserInfo) {
  pendingAction = async () => {
    await API.changeUserRole(u.id, !u.is_admin)
    await loadUsers()
  }
  confirmDialogTitle.value = u.is_admin ? 'Make User Regular' : 'Make User Admin'
  confirmDialogMessage.value = u.is_admin 
    ? `Are you sure you want to remove admin privileges from ${u.username}?`
    : `Are you sure you want to make ${u.username} an admin?`
  showConfirmDialog.value = true
}

function resetPasswordDialog(u: UserInfo) {
  selectedUser.value = u
  newPassword.value = ''
  resetPasswordError.value = ''
  showResetPasswordDialog.value = true
}

async function confirmResetPassword() {
  resetPasswordError.value = ''
  
  if (!newPassword.value || newPassword.value.length < 6) {
    resetPasswordError.value = 'Password must be at least 6 characters'
    return
  }

  if (!selectedUser.value) return

  isResettingPassword.value = true
  try {
    await API.setUserPassword(selectedUser.value.id, newPassword.value)
    showResetPasswordDialog.value = false
    selectedUser.value = null
  } catch (error: any) {
    resetPasswordError.value = error.response?.data?.error || 'Failed to reset password'
  } finally {
    isResettingPassword.value = false
  }
}

function deleteUserDialog(u: UserInfo) {
  pendingAction = async () => {
    await API.deleteUser(u.id)
    await loadUsers()
  }
  confirmDialogTitle.value = 'Delete User'
  confirmDialogMessage.value = `Are you sure you want to delete ${u.username}? This action cannot be undone.`
  showConfirmDialog.value = true
}

async function confirmAction() {
  if (!pendingAction) return
  
  isProcessing.value = true
  try {
    await pendingAction()
    showConfirmDialog.value = false
    pendingAction = null
  } catch (error) {
    console.error('Action failed:', error)
  } finally {
    isProcessing.value = false
  }
}
</script>

<style scoped>
.page-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.action-bar {
  margin-bottom: 2rem;
}

.users-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.table-header {
  display: grid;
  grid-template-columns: 1fr 0.5fr 0.8fr 2fr;
  gap: 1rem;
  padding: 1rem;
  background: var(--theme-color-5);
  font-weight: 600;
  border-bottom: 2px solid var(--theme-color-5);
}

.table-row {
  display: grid;
  grid-template-columns: 1fr 0.5fr 0.8fr 2fr;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid var(--theme-color-5);
  align-items: center;
}

.table-row:last-child {
  border-bottom: none;
}

.role-badge {
	display: inline-block;
	padding: 0.25rem 0.75rem;
	background: var(--theme-color-5);
	/* color: #333; */
	border-radius: 20px;
	font-size: 0.85rem;
	font-weight: 600;
	&.admin {
		background: var(--theme-color-2);
		color: var(--theme-color-5);
	}
}

.col-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 8px;
  color: #999;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(.25em);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
}

.modal h2 {
  margin-top: 0;
  margin-bottom: 1.5rem;
  color: #333;
}

.modal p {
  color: #666;
  margin-bottom: 1.5rem;
}

.modal-form {
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 500;
}

.form-group input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;
}

.error {
  display: block;
  color: #ff6b6b;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}

.modal-buttons {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .table-header,
  .table-row {
    grid-template-columns: 1fr;
  }

  .col-actions {
    flex-direction: column;
  }

  .small.btn {
    width: 100%;
  }
}
</style>
